import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  AfterViewInit,
  OnChanges,
} from '@angular/core';
import { FilterMetadata, MenuItem, PrimeNGConfig, SortMeta } from 'primeng/api';
import { Table } from 'primeng/table';

import { EnumFilterComponent } from './enum-filter/enum-filter.component';
import { ITableHeader } from 'src/app/sharedFeatures/models/itable-header';
import { TableHeaderType } from 'src/app/sharedFeatures/enum/table/table-header-type';
import { TableViewMood } from 'src/app/sharedFeatures/enum/table/table-view-mode';
import { hasProperty } from 'src/app/sharedFeatures/functions/key-at-list-check';
import { groupBy } from 'src/app/sharedFeatures/functions/group-column-by';
import { TranslateService } from '@ngx-translate/core';
import { AuthGuard } from 'src/app/sharedFeatures/services/auth-guard.service';
import { Observable } from 'rxjs/internal/Observable';
import { modifyDateValues } from './helpers';
import { Colors } from 'src/app/sharedFeatures/constants/colors';
import { ExportColumn } from '../../../../sharedFeatures/models/export-column';
import { ExcelExportService } from 'src/app/sharedFeatures/services/excel-export.service';
import { ApprovalType } from 'src/app/sharedFeatures/enum/table/approvalType';

@Component({
  selector: 'msn-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
})
export class GridComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() hasGrouping: boolean = false; // Default to false
  @Input() hasExport: boolean = false; // Default to false
  @Input() columns: ITableHeader[] = [];
  @Input() columnsGroup: ITableHeader[] = [];
  @Input() data: any[];
  @Input() exportData: any[];
  @Input() actions: MenuItem[] = [];
  @Input() selectedRecords: any[] = [];
  @Input() pageIndex: number = 0;
  @Input() paginator: boolean = true;
  @Input() sortable: boolean = true;
  @Input() canSelect: boolean = false;
  @Input() showGroupMenu: boolean = false;
  @Input() showSelectMenue: boolean = false;
  @Input() isRowClickable: boolean = true;
  @Input() pageSize: number = 6;
  @Input() rowsPerPageOptions: number[] = [5, 10, 20, 50, 100];
  @Input() totalRecordsLength: number;
  @Input() selectionMode: string;
  @Input() styleClass: string = 'p-datatable-gridlines';
  @Input() moduleTitle: string;
  @Input() module!: string;
  @Input() isFlag: Boolean = false;
  @Input() actionColumnTitle: string = 'shared.action'; // Default to 'Actions'

  @Input() actionButtonClass: string =
    'aube-btn-outline rounded-circle btn-color-lightblue';
  @Input() scrollable: boolean;
  @Input() responsive: boolean = false;
  @Input() sendDateOnlyAtFilter: boolean = true;
  @Input() lazy: boolean = true; // Default to server-side (lazy) mode

  @Output() filterChanged: EventEmitter<{
    [s: string]: FilterMetadata[];
  }> = new EventEmitter<{ [s: string]: FilterMetadata[] }>();

  // Emitted when a column's server-side date-range filter is applied.
  // from/to are null when the range is cleared.
  @Output() dateRangeFilterChanged = new EventEmitter<{
    field: string;
    from: Date | null;
    to: Date | null;
  }>();

  @Output() showGroupMenuChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() pageIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() pageSizeChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() FlagEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() sortChanged: EventEmitter<SortMeta[]> = new EventEmitter<
    SortMeta[]
  >();

  @Output() selectedRecordsChanged: EventEmitter<any[]> = new EventEmitter<
    any[]
  >();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter<any>();

  @Output() cellClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() ChangesForFlag: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() activationToggled: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('ADVGrid') ADVGrid: Table;
  @ViewChildren(EnumFilterComponent)
  enumFilters: QueryList<EnumFilterComponent>;
  hideColumnOptions: ITableHeader[] = [];
  approvalTypes = ApprovalType;

  dataList: any[];
  groupedData: any;
  recordKey: string = '';
  currentPageReportTemplate: string = '';
  groupColumnsOptions: any[];
  propertyGetters: ((item: any) => any)[]; // usedIn Grouping
  selectedGroupByCol: any[];
  viewMood: TableViewMood = TableViewMood.RecordsView;
  firstIndex: number = 0;
  isLoading: boolean = false;

  TableHeaderType: any = TableHeaderType;
  TableViewMood = TableViewMood;
  colors: string[] = Colors;
  exportColumns!: ExportColumn[];

  constructor(
    private adminGuard: AuthGuard,
    private config: PrimeNGConfig,
    private translateService: TranslateService,
    private excelExport: ExcelExportService
  ) {
    /**
     * intuitional
     */
  }

  ngOnInit(): void {
    this.getGroupByColumnOptions();
    this.getHideOptionsColumn();
    //this.columns = this.columns.filter(x => x.field != 'id' && !x.hidden);

    this.columns = this.columns.filter(x => !x.hidden);
    // NOTE: we intentionally do NOT overwrite `this.columns[i].name` with the
    // resolved string from hideColumnOptions. Header cells render via
    // `{{ col.name | translate }}`, so `name` must stay as the translation
    // key. Baking the resolved string in here was the cause of headers not
    // re-rendering on language change without a page refresh.
    this.dataList = JSON.parse(JSON.stringify(this.data));
    this.setPaginationTitle();
    this.getRecordKey();
    this.getFilterMenuTranslation();
    this.translateService.onLangChange.subscribe(res => {
      this.isLoading = true;
      this.getFilterMenuTranslation();
      // Re-resolve hideColumnOptions labels so the column-toggle dropdown
      // (which binds `optionLabel="name"` without a translate pipe) also
      // updates immediately. Same for exportColumns titles.
      this.getHideOptionsColumn();
      this.exportColumns = this.columns.map(col => ({
        title: this.translateService.instant(col.name),
        dataKey: col.field,
      }));
      setTimeout(() => {
        this.isLoading = false;
      }, 200);
    });
    this.selectedGroupByCol = this.columnsGroup;
    this.onGroupByChanged(this.selectedGroupByCol);
    this.exportColumns = this.columns.map(col => ({
      // Resolve the translation key at use-time so exports reflect the
      // current language (col.name is now kept as a key, not a frozen string).
      title: this.translateService.instant(col.name),
      dataKey: col.field,
    }));
  }

  getApprovalType(val: any) {
    return val;
  }

  exportExcel2() {
    const visibleColumns = this.columns.filter(col => !col.hidden);
    const rows = this.ADVGrid.filteredValue || this.exportData;

    this.excelExport
      .downloadSheet({
        fileName: `${this.currentPageReportTemplate}_${new Date().getTime()}`,
        sheetName: 'data',
        rightToLeft: this.translateService.currentLang === 'ar',
        // Header text is resolved at export time so it follows the active
        // language, matching how the grid renders col.name.
        columns: visibleColumns.map(col => ({
          header: this.translateService.instant(col.name),
          key: col.field,
        })),
        rows,
      })
      .catch(error => console.error('Error exporting to Excel:', error));
  }

  ngAfterViewInit(): void {
    this.listeningGroupingMenu();
  }

  getHideOptionsColumn(): void {
    this.hideColumnOptions = JSON.parse(JSON.stringify(this.columns));
    this.hideColumnOptions = this.hideColumnOptions.filter(
      col => col.name.toLowerCase() != 'id' && !col.hidden
    );
    this.hideColumnOptions.forEach(col => {
      col.name = this.translateService.instant(col.name);
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    ////

    if (!changes['data']?.firstChange && changes['data']?.currentValue) {
      this.dataList = JSON.parse(JSON.stringify(changes['data'].currentValue));
      this.setPaginationTitle();

      if (this.viewMood === TableViewMood.GroupView) {
        this.dataList = groupBy(this.data, [...this.propertyGetters]) as any;
      }
    }

    // Parents reassign the columns array when the active language changes
    // (to swap en*/ar* field names). The hidden-column filter only ran in
    // ngOnInit, so without re-running it here the freshly-assigned array
    // would re-introduce columns flagged hidden: true (e.g. the recordKey
    // 'id' helper). Re-apply the same filter and refresh the derived
    // collections so the table, column-toggle dropdown and export stay
    // consistent with the post-init shape.
    if (changes['columns'] && !changes['columns'].firstChange) {
      this.columns = this.columns.filter(x => !x.hidden);
      this.getRecordKey();
      this.getHideOptionsColumn();
      this.exportColumns = this.columns.map(col => ({
        title: this.translateService.instant(col.name),
        dataKey: col.field,
      }));
    }

    if (changes['pageIndex']?.currentValue) this.setFirstIndex();
  }

  getFilterMenuTranslation(): void {
    this.translateService.get('primeng.filter').subscribe((res: any) => {
      this.config.setTranslation(res);
    });
  }

  onPageChange(event: { first: number; rows: number }): void {
    const pageIndex = event.first / event.rows;
    const pageSizeChanged = event.rows !== this.pageSize;

    // Keep the local state in sync so the next onPage compares against the
    // rows/page the table is actually showing, even if the parent does not
    // push the inputs back down.
    this.pageSize = event.rows;
    this.pageIndex = pageIndex;

    // Changing the rows-per-page fires a single onPage event, and both
    // outputs are emitted for it (pageSizeChange first, so the new size is
    // applied before the page index). pageIndexChange is the ONE reload
    // trigger: parents must only update state in their pageSizeChange
    // handler and never fetch there, otherwise the same page is requested
    // twice. PrimeNG keeps the user near their current offset when the rows
    // change, so the index emitted below is the page that is actually shown.
    if (pageSizeChanged) {
      this.pageSizeChange.emit(event.rows);
    }

    this.pageIndexChange.emit(pageIndex);
  }
  setFirstIndex(): void {
    this.firstIndex = this.pageIndex * this.pageSize;
  }
  //{ filteredValue: any[];filters: { [s: string]: FilterMetadata[] };}
  onFilterChange(event: any): void {
    if (this.sendDateOnlyAtFilter) modifyDateValues(event.filters);
    this.filterChanged.emit(event.filters);
  }

  onDateRangeFilter(
    field: string,
    range: { from: Date | null; to: Date | null }
  ): void {
    this.dateRangeFilterChanged.emit({ field, ...range });
  }

  onSort(event: SortMeta): void {
    // Only emit sortChanged event in lazy mode (server-side sorting)
    // In non-lazy mode, PrimeNG handles sorting automatically on client-side
    if (this.lazy && this.isColumnSortable(event?.field)) {
      this.sortChanged.emit([event]);
    }
  }

  isColumnSortable(felid: string) {
    const col = this.columns.find(col => col.field === felid);
    return !col?.removeSorting;
  }
  onRowClicked(event: number | string): void {
    this.rowClicked.emit(event);
  }

  saveReorderedColumns(event: any) {}

  onSelectionChange(event: any): void {
    if (
      hasProperty(this.selectedRecords, 'key') &&
      this.viewMood === TableViewMood.GroupView
    )
      this.selectedRecords = JSON.parse(JSON.stringify(this.data));
    this.selectedRecordsChanged.emit(this.selectedRecords);
  }

  onTakeAction(item: MenuItem, recordData: any): void {
    if (recordData.id && item.command) item.command(recordData);
  }
  onRowClick(rowData: any): void {
    this.cellClicked.emit(rowData);
  }

  getRecordKey(): void {
    this.recordKey = this.columns.find(col => col.recordKey)?.field || '';
  }

  getGroupByColumnOptions(): void {
    this.groupColumnsOptions = this.columns.filter(item => item.canGroup);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onGroupByChanged(col: ITableHeader[]): void {
    if (!col.length) {
      this.reset();
      return;
    }
    this.propertyGetters = col.map((key: any) => {
      return (item: any) => item[key.field];
    });

    this.dataList = groupBy(this.data, [...this.propertyGetters]) as any;
    this.viewMood = TableViewMood.GroupView;
  }

  reset(): void {
    this.selectedGroupByCol = [];
    this.viewMood = TableViewMood.RecordsView;
    //this.ADVGrid.reset();
    this.enumFilters?.forEach(enumFilter => {
      enumFilter.reset();
    });
    this.dataList = this.data;
  }

  getEnumKeyByEnumValue(
    enumObject: { [key: string]: string },
    enumValue: string
  ): string {
    const key = Object.keys(enumObject).find(k => enumObject[k] == enumValue);
    return key !== undefined ? key : '';
  }

  setPaginationTitle(): void {
    if (this.totalRecordsLength) {
      this.currentPageReportTemplate = `${this.totalRecordsLength
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}  ${this.moduleTitle}`;
    } else {
      // Handle the case where `this.totalRecordsLength` is undefined
      this.currentPageReportTemplate = `0  ${this.moduleTitle}`;
    }
  }
  actionHasPermission(action: any): Observable<boolean> | boolean {
    if (action.permission) {
      return this.adminGuard.HasPermission([action.permission]);
    } else {
      return true;
    }
  }

  listeningGroupingMenu(): void {
    const groupingModal = document.getElementById('GroupingMenu');
    const togglerButton = document.getElementById('GroupingMenuToggler');

    if (!groupingModal || !togglerButton) {
      return;
    }

    document.addEventListener('click', (event: MouseEvent) => {
      setTimeout(() => {
        if (
          event.target === togglerButton ||
          (event.target instanceof Element &&
            event.target.closest('#GroupingMenuToggler'))
        ) {
          return;
        }

        if (!groupingModal.contains(event.target as Node)) {
          groupingModal.classList.remove('active');
        }
      }, 0);
    });
  }

  transform(value: string): string {
    if (!value) return '';

    const words = value.trim().split(' ');

    // Take only the first two words (or fewer if there are less than two words)
    const firstTwoWords = words.slice(0, 2);

    return firstTwoWords.map(word => word.charAt(0).toUpperCase()).join('');
  }

  confirmDialog(data: any) {
    this.FlagEvent.emit(data);
  }
  changesForFlagEvent(event: any) {
    // console.log("event ChangesForFlag: ",event.checked);

    this.ChangesForFlag.emit(event.checked);
  }

  // Default status color method - matching status card palette from color-palate.scss
  getStatusColor(statusId: string): string {
    switch (statusId?.toLowerCase()) {
      case 'pending':
        return '#487193'; // $color-status-card-4
      case 'request for modification':
        return '#DAEBB2'; // $color-status-card-7
      case 'in progress':
        return '#F5E37F'; // $color-status-card-6
      case 'completed':
        return '#8DB381'; // $color-status-card-8
      case 'not applicable':
      case 'not applicable by ph':
        return '#0E1F2F'; // $color-status-card-5
      case 'on hold':
        return '#D9D9D9'; // $color-status-card-1
      case 'closed':
        return '#9E9E9E'; // $color-status-card-2
      case 'reopen':
        return '#CADEEB'; // $color-status-card-3
      default:
        return '#942B2B'; // $color-status-card-9 (Overdue)
    }
  }
  onClickAction(column: any, rowData: any, event?: Event): void {
    // Stop the click from bubbling to the row's (click) handler (which would
    // navigate/select); a click-action is a distinct, self-contained action.
    event?.stopPropagation();
    if (column.clickHandler) {
      column.clickHandler(rowData);
    }
  }
}
